import React, { useState, useEffect } from "react";
import { useMetaMaskConnection } from "../../hooks/useMetaMaskConnection";
import metaMaskConnectionManager, {
  resetMetaMaskConnection,
} from "../../utils/metamask-connection-manager";
import {
  resetMetaMaskState,
  getMetaMaskStateInfo,
} from "../../utils/metamask-state-cleaner";
import styles from "./MetaMaskErrorRecovery.module.scss";

interface MetaMaskErrorRecoveryProps {
  onClose?: () => void;
  autoShow?: boolean;
}

const MetaMaskErrorRecovery: React.FC<MetaMaskErrorRecoveryProps> = ({
  onClose,
  autoShow = false,
}) => {
  const [isVisible, setIsVisible] = useState(autoShow);
  const [isResetting, setIsResetting] = useState(false);
  const [resetSteps, setResetSteps] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const { connect, isConnecting, error, clearError } = useMetaMaskConnection();

  // Auto-show when MetaMask connection errors occur
  useEffect(() => {
    const handleMetaMaskError = (event: any) => {
      if (
        event.detail?.error?.code === -32002 ||
        event.detail?.error?.message?.includes("Connection declined") ||
        event.detail?.error?.message?.includes("already pending")
      ) {
        setIsVisible(true);
      }
    };

    window.addEventListener("metamask-error", handleMetaMaskError);
    return () =>
      window.removeEventListener("metamask-error", handleMetaMaskError);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    clearError();
    onClose?.();
  };

  const handleReset = async () => {
    setIsResetting(true);
    setResetSteps([]);
    setShowDetails(true);

    try {
      // Use comprehensive state reset
      const result = await resetMetaMaskState();
      setResetSteps(result.steps);

      // Also reset the connection manager state
      resetMetaMaskConnection();

      // Clear any error state
      clearError();

      if (result.success) {
        console.log("MetaMask state reset completed successfully");
      } else {
        console.warn(
          "MetaMask state reset completed with errors:",
          result.errors
        );
      }
    } catch (error) {
      console.error("Failed to reset MetaMask state:", error);
      setResetSteps((prev) => [...prev, `Error: ${error}`]);
    } finally {
      setIsResetting(false);
    }
  };

  const handleRetryConnection = async () => {
    clearError();
    await handleReset();

    // Small delay before attempting connection
    setTimeout(() => {
      connect();
    }, 1000);
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  if (!isVisible && !error) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>MetaMask Connection Issue</h3>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.errorIcon}>🦊</div>

          <div className={styles.message}>
            <p>
              {error ||
                "MetaMask connection was declined or is experiencing issues."}
            </p>

            <div className={styles.explanation}>
              <p>
                <strong>This usually happens when:</strong>
              </p>
              <ul>
                <li>
                  A previous connection request is still pending in MetaMask
                </li>
                <li>Multiple connection attempts were made too quickly</li>
                <li>MetaMask needs to be refreshed</li>
              </ul>
            </div>
          </div>

          <div className={styles.actions}>
            <button
              className={`${styles.button} ${styles.primary}`}
              onClick={handleRetryConnection}
              disabled={isConnecting || isResetting}
            >
              {isConnecting ? "Connecting..." : "Reset & Retry Connection"}
            </button>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleReset}
              disabled={isResetting || isConnecting}
            >
              {isResetting ? "Resetting..." : "Reset Connection State"}
            </button>

            <button
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleRefreshPage}
            >
              Refresh Page
            </button>
          </div>

          {showDetails && resetSteps.length > 0 && (
            <div className={styles.resetDetails}>
              <h4>Reset Progress:</h4>
              <div className={styles.stepsList}>
                {resetSteps.map((step, index) => (
                  <div key={index} className={styles.step}>
                    <span className={styles.stepNumber}>{index + 1}</span>
                    <span className={styles.stepText}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.tips}>
            <h4>Additional Tips:</h4>
            <ul>
              <li>Check if MetaMask popup is hidden behind other windows</li>
              <li>Try disconnecting and reconnecting in MetaMask settings</li>
              <li>Restart your browser if the issue persists</li>
              <li>Make sure you have the latest version of MetaMask</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetaMaskErrorRecovery;
